import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, EyeOff, Users, Briefcase, MapPin, Clock, IndianRupee, FileText, ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { useCurrency } from '@/contexts/CurrencyContext';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPARTMENTS = ['Engineering', 'HR', 'Marketing', 'Sales', 'Finance', 'Operations', 'Design', 'Legal', 'Support', 'Other'];

const emptyJob = { title: '', department: '', location: '', description: '', requirements: '', salary_min: '', salary_max: '', status: 'open', is_public: true, assigned_recruiter_id: '' };

export default function JobPostings() {
  const { symbol: curSymbol, currency: curCode } = useCurrency();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [form, setForm] = useState(emptyJob);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewJob, setPreviewJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [appsJobTitle, setAppsJobTitle] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [recruiters, setRecruiters] = useState([]);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = storedUser.role === 'admin' || storedUser.role === 'team_leader';

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jobs`, { headers: getHeaders() });
      setJobs(res.data);
    } catch { toast.error('Failed to fetch jobs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchJobs(); fetchRecruiters(); }, []);

  const fetchRecruiters = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: getHeaders() });
      setRecruiters(res.data.filter(u => u.is_active !== false));
    } catch (err) { console.error("./pages/JobPostings.jsx:", err); }
  };

  const openCreate = () => { setForm(emptyJob); setEditId(null); setFormOpen(true); };

  const openEdit = (job) => {
    setForm({ title: job.title, department: job.department, location: job.location, description: job.description, requirements: job.requirements, salary_min: job.salary_min || '', salary_max: job.salary_max || '', status: job.status, is_public: job.is_public !== false, assigned_recruiter_id: job.assigned_recruiter_id || '' });
    setEditId(job.id);
    setFormOpen(true);
  };

  const handlePreview = () => {
    if (!form.title) { toast.error('Title is required to preview'); return; }
    setPreviewJob({ ...form, created_at: new Date().toISOString() });
    setPreviewOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.department || !form.location) {
      toast.error('Title, Department, and Location are required'); return;
    }
    setSaving(true);
    try {
      const selectedRecruiter = recruiters.find(r => r.id === form.assigned_recruiter_id);
      const payload = { ...form, salary_min: form.salary_min ? Number(form.salary_min) : null, salary_max: form.salary_max ? Number(form.salary_max) : null, salary_currency: curCode, assigned_recruiter_name: selectedRecruiter?.name || '' };
      if (editId) {
        await axios.put(`${API}/jobs/${editId}`, payload, { headers: getHeaders() });
        toast.success('Job updated');
      } else {
        await axios.post(`${API}/jobs`, payload, { headers: getHeaders() });
        toast.success('Job created');
      }
      setFormOpen(false); fetchJobs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This will also delete all applications.`)) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}`, { headers: getHeaders() });
      toast.success('Job deleted'); fetchJobs();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleStatus = async (job) => {
    const newStatus = job.status === 'open' ? 'closed' : 'open';
    try {
      await axios.put(`${API}/jobs/${job.id}`, { ...job, status: newStatus }, { headers: getHeaders() });
      toast.success(`Job ${newStatus === 'open' ? 'reopened' : 'closed'}`); fetchJobs();
    } catch { toast.error('Failed to update status'); }
  };

  const viewApplications = async (job) => {
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/applications`, { headers: getHeaders() });
      setApplications(res.data);
      setAppsJobTitle(job.title);
      setAppsOpen(true);
    } catch { toast.error('Failed to fetch applications'); }
  };

  const filteredJobs = jobs.filter(j => {
    if (filterStatus !== 'all' && j.status !== filterStatus) return false;
    const q = searchText.trim().toLowerCase();
    if (!q) return true;
    return (j.title || '').toLowerCase().includes(q)
      || (j.department || '').toLowerCase().includes(q)
      || (j.location || '').toLowerCase().includes(q)
      || (j.description || '').toLowerCase().includes(q)
      || (j.assigned_recruiter_name || '').toLowerCase().includes(q);
  });
  // Per-tenant careers URL — uses the tenant slug from the saved login response so
  // the URL reflects the company/client name, not the Recruitizr brand.
  const tenantInfo = (() => { try { return JSON.parse(localStorage.getItem('tenant') || '{}'); } catch { return {}; } })();
  const tenantSlug = tenantInfo.slug || '';
  const careersPath = tenantSlug ? `/careers/${tenantSlug}` : '/careers';
  const careersUrl = `${window.location.origin}${careersPath}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="job-postings-title" style={{ fontFamily: 'Manrope, sans-serif' }}>{isAdmin ? 'Job Postings' : 'Career Responses'}</h1>
          <p className="text-slate-600">{isAdmin ? 'Manage job listings for Talent@HRassistance' : 'View applications for your assigned jobs'}</p>
        </div>
        <div className="flex gap-2 items-center">
          <a href={careersPath} target="_blank" rel="noreferrer" data-testid="view-careers-link">
            <Button variant="outline" className="border-blue-200 text-blue-700 rounded font-bold"><Eye size={16} className="mr-2" />View Careers Page</Button>
          </a>
          {isAdmin && (
            <Button data-testid="create-job-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              <Plus size={16} className="mr-2" />New Job
            </Button>
          )}
        </div>
      </div>

      {/* Careers link */}
      {isAdmin && (
      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 flex items-center justify-between">
        <div className="text-sm text-blue-800">
          <span className="font-bold">Public Careers URL:</span>{' '}
          <a href={careersPath} target="_blank" rel="noreferrer" className="underline">{careersUrl}</a>
        </div>
        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(careersUrl); toast.success('Link copied!'); }} className="text-blue-600 text-xs font-bold">Copy Link</Button>
      </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="jobs-search-input" placeholder="Search jobs by title, department, location, description…" value={searchText} onChange={e => setSearchText(e.target.value)} className="pl-9 rounded border-blue-200" />
        </div>
        <select data-testid="job-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-9 rounded border border-blue-200 bg-white px-3 text-sm">
          <option value="all">All ({jobs.length})</option>
          <option value="open">Open ({jobs.filter(j => j.status === 'open').length})</option>
          <option value="closed">Closed ({jobs.filter(j => j.status === 'closed').length})</option>
        </select>
      </div>

      {/* Jobs Table */}
      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Title</TableHead>
              <TableHead className="font-bold text-blue-900">Department</TableHead>
              <TableHead className="font-bold text-blue-900">Location</TableHead>
              <TableHead className="font-bold text-blue-900">Salary Range</TableHead>
              <TableHead className="font-bold text-blue-900">Status</TableHead>
              <TableHead className="font-bold text-blue-900">Visibility</TableHead>
              <TableHead className="font-bold text-blue-900">Assigned To</TableHead>
              <TableHead className="font-bold text-blue-900">Applications</TableHead>
              <TableHead className="font-bold text-blue-900">Posted</TableHead>
              <TableHead className="font-bold text-blue-900">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
            ) : filteredJobs.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8">
                <Briefcase size={48} className="mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500" data-testid="no-jobs-msg">No job postings yet</p>
              </TableCell></TableRow>
            ) : filteredJobs.map(j => (
              <TableRow key={j.id} data-testid={`job-row-${j.id}`} className="hover:bg-blue-50">
                <TableCell>
                  <span className="font-semibold text-blue-900">{j.title}</span>
                </TableCell>
                <TableCell className="text-sm">{j.department}</TableCell>
                <TableCell className="text-sm flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{j.location}</TableCell>
                <TableCell className="text-sm">
                  {j.salary_min || j.salary_max ? (
                    <span className="flex items-center gap-0.5"><span className="text-[10px] font-semibold">{curSymbol}</span>{j.salary_min ? (curCode === 'INR' ? `${(j.salary_min/100000).toFixed(1)}L` : `${(j.salary_min/1000).toFixed(0)}K`) : '—'} – {j.salary_max ? (curCode === 'INR' ? `${(j.salary_max/100000).toFixed(1)}L` : `${(j.salary_max/1000).toFixed(0)}K`) : '—'}</span>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {isAdmin ? (
                    <button data-testid={`toggle-status-${j.id}`} onClick={() => toggleStatus(j)} className={`text-xs font-bold px-2.5 py-1 rounded ${j.status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                      {j.status === 'open' ? 'Open' : 'Closed'}
                    </button>
                  ) : (
                    <span className={`text-xs font-bold px-2.5 py-1 rounded ${j.status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                      {j.status === 'open' ? 'Open' : 'Closed'}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <span data-testid={`job-visibility-${j.id}`} className={`text-xs font-bold px-2 py-1 rounded ${j.is_public !== false ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                    {j.is_public !== false ? 'Public' : 'Internal'}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {j.assigned_recruiter_name || <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell>
                  <button data-testid={`view-apps-${j.id}`} onClick={() => viewApplications(j)} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-bold">
                    <Users size={13} />{j.applications_count || 0}
                  </button>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{j.created_at?.slice(0, 10)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {isAdmin && <Button size="sm" variant="ghost" data-testid={`edit-job-${j.id}`} onClick={() => openEdit(j)} className="text-blue-600 hover:text-blue-800"><Edit2 size={14} /></Button>}
                    {isAdmin && <Button size="sm" variant="ghost" data-testid={`delete-job-${j.id}`} onClick={() => handleDelete(j)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></Button>}
                    {!isAdmin && <Button size="sm" variant="ghost" onClick={() => viewApplications(j)} className="text-blue-600 hover:text-blue-800 text-xs font-bold"><Eye size={14} className="mr-1" />View</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="rounded max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">{editId ? 'Edit Job' : 'Create New Job'}</DialogTitle>
            <DialogDescription>{editId ? 'Update the job posting details.' : 'Fill in the details to create a new job posting.'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Job Title *</Label>
                <Input data-testid="job-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1 rounded border-blue-200" placeholder="e.g., Senior Software Engineer" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Department *</Label>
                <select data-testid="job-dept-input" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} required className="mt-1 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                  <option value="">Select Department</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Location *</Label>
                <Input data-testid="job-location-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} required className="mt-1 rounded border-blue-200" placeholder="e.g., Mumbai, Remote" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Salary Min ({curCode})</Label>
                <Input data-testid="job-salary-min" type="number" value={form.salary_min} onChange={e => setForm({ ...form, salary_min: e.target.value })} className="mt-1 rounded border-blue-200" placeholder="e.g., 800000" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Salary Max ({curCode})</Label>
                <Input data-testid="job-salary-max" type="number" value={form.salary_max} onChange={e => setForm({ ...form, salary_max: e.target.value })} className="mt-1 rounded border-blue-200" placeholder="e.g., 1500000" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
                <select data-testid="job-status-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="mt-1 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Show on Careers Page</Label>
                <div className="mt-1 flex items-center gap-3 h-10">
                  <button type="button" data-testid="job-public-toggle"
                    onClick={() => setForm({ ...form, is_public: !form.is_public })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_public ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_public ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className={`text-sm font-semibold ${form.is_public ? 'text-emerald-700' : 'text-slate-500'}`}>
                    {form.is_public ? 'Live on Careers' : 'Internal Only'}
                  </span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Assign Recruiter</Label>
                <select data-testid="job-recruiter-select" value={form.assigned_recruiter_id} onChange={e => setForm({ ...form, assigned_recruiter_id: e.target.value })} className="mt-1 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                  <option value="">Unassigned</option>
                  {recruiters.map(r => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
                </select>
                <div className="mt-1">
                  <SearchableSelect value={form.assigned_recruiter_id} onChange={(id) => setForm({ ...form, assigned_recruiter_id: id })}
                    items={recruiters} getSublabel={(r) => r.role}
                    emptyLabel="Unassigned" placeholder="Search recruiter…" testId="job-recruiter"
                    allowClear noneLabel="— Unassigned —" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Description</Label>
              <textarea data-testid="job-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm" placeholder="Job description, responsibilities, etc." />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Requirements</Label>
              <textarea data-testid="job-req-input" value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} rows={3} className="mt-1 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm" placeholder="Skills, qualifications, experience required..." />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" data-testid="preview-job-btn" onClick={handlePreview} className="border-blue-200 text-blue-700 rounded font-bold">
                <Eye size={16} className="mr-2" />Preview
              </Button>
              <Button type="submit" data-testid="save-job-btn" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
                {saving ? 'Saving...' : (editId ? 'Update Job' : 'Publish Job')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Preview: How candidates will see this</DialogTitle>
            <DialogDescription>This is a preview of how the job will appear on the public careers page.</DialogDescription>
          </DialogHeader>
          {previewJob && (
            <div className="mt-4 border border-blue-100 rounded p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-1">{previewJob.title}</h2>
              <div className="flex flex-wrap gap-3 text-sm text-slate-600 mb-4">
                <span className="flex items-center gap-1"><Briefcase size={14} />{previewJob.department}</span>
                <span className="flex items-center gap-1"><MapPin size={14} />{previewJob.location}</span>
                {(previewJob.salary_min || previewJob.salary_max) && (
                  <span className="flex items-center gap-1"><span className="font-semibold">{curSymbol}</span>{previewJob.salary_min ? (curCode === 'INR' ? `${(Number(previewJob.salary_min)/100000).toFixed(1)}L` : `${(Number(previewJob.salary_min)/1000).toFixed(0)}K`) : '—'} – {previewJob.salary_max ? (curCode === 'INR' ? `${(Number(previewJob.salary_max)/100000).toFixed(1)}L` : `${(Number(previewJob.salary_max)/1000).toFixed(0)}K`) : '—'}</span>
                )}
                <span className="flex items-center gap-1"><Clock size={14} />Posted today</span>
              </div>
              {previewJob.description && <><h3 className="font-bold text-blue-900 mb-1">Description</h3><p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">{previewJob.description}</p></>}
              {previewJob.requirements && <><h3 className="font-bold text-blue-900 mb-1">Requirements</h3><p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">{previewJob.requirements}</p></>}
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold w-full" disabled>Apply Now (Preview)</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Applications Dialog */}
      <Dialog open={appsOpen} onOpenChange={setAppsOpen}>
        <DialogContent className="rounded max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Applications — {appsJobTitle}</DialogTitle>
            <DialogDescription>{applications.length} application(s) received</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {applications.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No applications yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-50">
                    <TableHead className="font-bold text-blue-900">Candidate</TableHead>
                    <TableHead className="font-bold text-blue-900">Email</TableHead>
                    <TableHead className="font-bold text-blue-900">Phone</TableHead>
                    <TableHead className="font-bold text-blue-900">Resume</TableHead>
                    <TableHead className="font-bold text-blue-900">Applied</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map(a => (
                    <TableRow key={a.id} data-testid={`app-row-${a.id}`}>
                      <TableCell className="font-semibold text-blue-900">{a.candidate_name}</TableCell>
                      <TableCell className="text-sm">{a.email}</TableCell>
                      <TableCell className="text-sm">{a.phone || '—'}</TableCell>
                      <TableCell>
                        {a.resume_url ? (
                          <a href={`${BACKEND_URL}${a.resume_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><FileText size={12} />{a.resume_filename || 'Download'}</a>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{a.applied_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
